import { useRouter } from "next/router";
import { useMemo } from "react";

import { SwitchCase } from "@enkor/toss-slash";

import WebViewBridge from "@logics/utils/webViewBridge";
import StackLayout from "@domains/common/views/layouts/StackLayout/StackLayout";
import { NAVIGATION_ROUTE } from "@domains/common/constants/navigationRoute";
import { useFetchProjectDetailQuery } from "@domains/project/network/projectQueries";
import { APP_SIZE } from "@styles/variables";
import { CtaType } from "@domains/project/types/project";
import { CTA_TYPE } from "@domains/project/constants/project";

import ProjectEventSection from "../sections/ProjectEventSection/ProjectEventSection";
import AvatarListSection from "../sections/AvatarListSection/AvatarListSection";
import ProjectDetailButton from "../components/ProjectEventDetailButton/ProjectEventDetailButton";
import CommentSection from "../sections/CommentSection/CommentSection";

import * as S from "./ProjectDetail.style";

interface ProjectDetailComponentProps {
  projectId: string;
}

const ProjectDetailComponent = ({ projectId }: ProjectDetailComponentProps) => {
  const { push, back } = useRouter();

  const { data: projectDetail, status } = useFetchProjectDetailQuery(projectId);

  const handleBack = () => {
    if (WebViewBridge.isInAppWebView()) {
      WebViewBridge.getInstance().navigateBack();
      return;
    }

    if (window.history.length > 1) {
      back();
    } else {
      push(NAVIGATION_ROUTE.EVENT_LIST.HREF);
    }
  };

  const ctaType: CtaType = useMemo(() => {
    if (!projectDetail) return CTA_TYPE.DEFAULT;

    if (projectDetail.isClosed) {
      return CTA_TYPE.CLOSED;
    } else if (projectDetail.hasPaid) {
      return CTA_TYPE.JOINED;
    } else if (projectDetail.isFull) {
      return CTA_TYPE.FULL;
    }

    return CTA_TYPE.UNJOINED;
  }, [projectDetail]);

  return (
    <StackLayout
      isFixedNavbar
      navbar={{
        iconType: "CHEVRON_LEFT",
        onClickIcon: handleBack,
        title: projectDetail?.title || "Project",
      }}
    >
      <SwitchCase
        value={status}
        caseBy={{
          pending: <div />,
          success: projectDetail && (
            <S.SectionContainer>
              <S.ContentImageContainer>
                {projectDetail.contentImageUrlList.map(({ url, fileId }) => (
                  <S.ContentImageWrapper key={fileId}>
                    <S.ContentImage
                      src={url}
                      alt={`project-detail-content-${fileId}`}
                      width={0}
                      height={0}
                      sizes={`${APP_SIZE.MAX_WIDTH}px`}
                    />
                  </S.ContentImageWrapper>
                ))}
              </S.ContentImageContainer>

              <S.ProjectDescriptionWrapper>
                <S.ProjectDescription name="m">
                  {projectDetail.description}
                </S.ProjectDescription>
              </S.ProjectDescriptionWrapper>

              <ProjectEventSection projectId={projectId} />

              <div>
                <AvatarListSection
                  maxMemberCount={projectDetail.maxMemberCount}
                  memberList={projectDetail.memberList || []}
                />

                <CommentSection
                  projectId={projectId}
                  hasJoined={projectDetail.hasPaid}
                />
              </div>
            </S.SectionContainer>
          ),
        }}
      />

      {projectDetail && (
        <ProjectDetailButton
          projectId={projectId}
          projectInfo={projectDetail}
          ctaType={ctaType}
          isProjectClosed={projectDetail.isClosed}
        />
      )}
    </StackLayout>
  );
};

export default ProjectDetailComponent;
